/** @jsx h */

import format from 'date-fns/format';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import { h } from 'preact';
import uuidv4 from 'uuid/v4';
import emoji from 'react-easy-emoji';
import { isAscii, toBinary } from './ascii-helper';
import { APPS_LIST, MAX_LAST_VISIT_HOURS, LOCAL_STORAGE, OS, USER_AUTH } from './consts';
// eslint-disable-next-line import/no-cycle
import { routes } from '../components/app';

const XMPP_DOMAIN = process.env.PREACT_APP_XMPP_DOMAIN;

const TIME_FORMAT = Intl.DateTimeFormat(
  undefined, {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  },
);
const TIME_FORMAT_OPTIONS = TIME_FORMAT.resolvedOptions();
const TIME_SHOULD_USE_AM_PM = Boolean(TIME_FORMAT_OPTIONS.hour12);
const SHORT_TIME_ZONE = (() => {
  const timeZonePart = TIME_FORMAT.formatToParts(Date.now()).find((part) => part.type === 'timeZoneName');
  return timeZonePart ? timeZonePart.value : TIME_FORMAT_OPTIONS.timeZone;
})();

export const generateJID = (id, isMain, uuid) => {
  let deviceId = localStorage.getItem('uuid');
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem('uuid', deviceId);
  }
  return isMain ? `${id}@${XMPP_DOMAIN}` : `${id}@${XMPP_DOMAIN}/${deviceId}_${uuid}`;
};

export function generateProfessionsArray(professions) {
  const Services = [];

  professions.forEach((profession) => {
    const Service = {
      name: profession.professions[0].text,
      categories: profession.professions[1].text.split(','),
    };
    Services.push(Service);
  });
  return Services;
}

export function checkIfLoggedIn() {
  return !!localStorage && localStorage.getItem(USER_AUTH);
}

export function convertDate(date = '') {
  const oldDate = new Date(date.split('.')[0]);

  return `${(oldDate.getMonth() + 1).toString().padStart(2, '0')}/${oldDate
    .getDate()
    .toString()
    .padStart(2, '0')}/${oldDate.getFullYear()} ${oldDate.getHours().toString().padStart(2, '0')}:${oldDate
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
}

export const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
export const mobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(
  navigator.userAgent,
);

export const convertTime = (date) => {
  const d = typeof date === 'string' || date instanceof String ? new Date(Date.parse(date)) : new Date(date);
  // eslint-disable-next-line no-restricted-globals
  return isNaN(d.getTime()) || !date
    ? '-'
    : `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

export const convertTimeSafari = (date) => {
  const b = date.split(/\D/);
  const d = new Date(Date.UTC(b[0], b[1] - 1, b[2], b[3], b[4], b[5]));
  return Number.isNaN(d.getTime()) || !date
    ? '-'
    : `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

export function changeDateISOFormat(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d
    .getDate()
    .toString()
    .padStart(2, '0')}`;
}

export function convertDuration(totalSeconds) {
  let minutes = parseInt((totalSeconds / 60).toString(), 10);
  let seconds = totalSeconds - minutes * 60;

  if (minutes.toString().length === 1) minutes = `0${minutes}`;
  if (seconds.toString().length === 1) seconds = `0${seconds}`;

  return `${minutes}:${seconds}`;
}

export function setEmphasizedText(content, elClass) {
  const emphasizedPosition = content.title.indexOf(content.emphasized);
  const emphasizedLen = content.emphasized.length;

  return emphasizedPosition + 1 ? (
    <h1 className={elClass}>
      {emoji(content.title.substring(0, emphasizedPosition))}
      <span>{emoji(content.emphasized)}</span>
      {emoji(content.title.substring(emphasizedPosition + emphasizedLen))}
    </h1>
  ) : (
    <h1 className={elClass}>{emoji(content.title)}</h1>
  );
}

export const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }

  return null;
};

export function bytesToSize(bytes) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';

  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString(), 10);

  return `${Math.round(bytes / 1024 ** i)} ${sizes[i]}`;
}

export function secToMS(sec = 0) {
  const s = sec % 60;
  const m = (sec - s) / 60;

  return { m: m.toString().padStart(2, '0'), s: s.toString().padStart(2, '0') };
}
export function getTotalPrice(timeObj, cpm) {
  return timeObj.s > 0 ? cpm * timeObj.m + cpm : cpm * timeObj.m;
}

export function getIntervalStep(val) {
  if (val <= 1) {
    return 0.01;
  }

  if (val <= 2) {
    return 0.05;
  }

  if (val <= 5) {
    return 0.1;
  }

  return 0.2;
}

export function queryToParams(query) {
  const q = query.replace(/^\??\//, '');
  return q.split('&').reduce((values, param) => {
    const [key, value] = param.split('=');

    return {
      ...values,
      [key]: value,
    };
  }, {});
}

/**
 *
 * @desc Returns haystack based array without needle if it wasn't included before and with needle otherwise
 *
 * @param {[*]} haystack
 * @param {*} needle
 *
 * @return {[*]}
 */
export function addIfNotExists(haystack, needle) {
  if (haystack.includes(needle)) {
    return haystack.filter((item) => item !== needle);
  }

  return [...haystack, needle];
}

export const getEncodedCredentials = ({ email, password }) => {
  const emailAsBinaryString = isAscii(email) ? email : toBinary(email);
  const passwordAsBinaryString = isAscii(password) ? password : toBinary(password);

  return `Basic ${window.btoa(`${emailAsBinaryString}:${passwordAsBinaryString}`)}`;
};

/**
 *
 * @desc Returns platform depended mobile app link
 *
 * @param {string} os
 *
 * @return {string}
 */
export const getMobileAppLink = (os) => APPS_LIST.find((app) => app.os === os) || APPS_LIST.find((app) => app.os === OS.IOS).link;

/**
 *
 * @param {boolean} isPro
 * @param {number | null} [refId]
 * @param {number | null} [userId]
 * @return {string}
 */
export function resolveAuthorisedEntryPoint(isPro, refId = null, userId = null) {
  const pathName = localStorage.getItem(LOCAL_STORAGE.PATH_NAME);
  localStorage.removeItem(LOCAL_STORAGE.PATH_NAME);

  if (refId) {
    return `${routes.USER_FOR_COMP}${userId}`;
  }

  if (pathName && Object.values(routes).includes(pathName)) {
    return pathName;
  }

  if (isPro) {
    return routes.MY_CLIENTS;
  }

  return routes.MY_PROS;
}

/**
 * @desc Returns formatted last visit for pro card or false if formatted last visit time isn't available
 *
 * @param {number} lastVisitTS
 * @param {number} minLastVisitMinutes
 * @return {string | boolean}
 */
export const getFormattedLastVisit = (lastVisitTS, minLastVisitMinutes) => {
  const lastVisitDate = new Date(lastVisitTS);
  const currentDate = new Date();

  const hours = Math.floor(differenceInHours(currentDate, lastVisitDate));
  const minutes = Math.floor(differenceInMinutes(currentDate, lastVisitDate));

  if (hours) {
    return hours < MAX_LAST_VISIT_HOURS && `${hours}h`;
  }

  if (minutes >= minLastVisitMinutes) {
    return `${minutes}m`;
  }

  return '';
};

/**
 *
 * @desc Converts bytes to mega bytes (MiB if more accurate)
 *
 * @param {number} bytes
 *
 * @return {number}
 */
export const getMBytesFromBytes = (bytes) => bytes / 1024 ** 2;

/**
 *
 * @param {number} timeStamp
 * @param {string} pattern
 * @return {string}
 */
export function getFormattedDate(timeStamp, pattern) {
  return format(new Date(timeStamp), pattern);
}

export function cancelEventBubbling(e) {
  e && typeof e.stopPropagation === 'function' && e.stopPropagation();
}

/**
 *
 * @param {number} hours
 * @param {number} minutes
 */
export function getFormattedTime(hours, minutes) {
  if (TIME_SHOULD_USE_AM_PM) {
    const ampmSuffix = hours >= 12 ? 'PM' : 'AM';
    const h12cycleHours = hours % 12 || 12;

    return `${String(h12cycleHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampmSuffix}`;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function getTimezoneName() {
  return SHORT_TIME_ZONE;
}



// WEBPACK FOOTER //
// ./utils/index.js